import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Upload, Trash2, Building2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState({ is_pkp: false, ppn_persen: 11 });
  const [p, setP] = useState({ name: "", address: "", phone: "", email: "", logo_b64: "" });
  const [loading, setLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);

  const load = async () => {
    const [sr, pr] = await Promise.all([api.get("/settings"), api.get("/workspace-profile")]);
    setS(sr.data); setP(pr.data);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setLoading(true);
    try { await api.put("/settings", { is_pkp: s.is_pkp, ppn_persen: Number(s.ppn_persen) }); toast.success("Pengaturan PKP tersimpan"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.put("/workspace-profile", { name: p.name, address: p.address, phone: p.phone, email: p.email });
      toast.success("Data invoice diperbarui");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const validateAndUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    if (file.size > 100 * 1024) { toast.error("Ukuran maksimum 100 KB"); return; }
    setLogoLoading(true);
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = async () => {
        if (img.width > 400 || img.height > 400) { setLogoLoading(false); toast.error("Dimensi maks 200×200 px (rekomendasi)"); return; }
        // Resize to 200x200 canvas
        const canvas = document.createElement("canvas");
        canvas.width = 200; canvas.height = 200;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 200, 200);
        const scale = Math.min(200 / img.width, 200 / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (200 - w) / 2, (200 - h) / 2, w, h);
        const dataUrl = canvas.toDataURL("image/png", 0.92);
        try {
          await api.post("/workspace-profile/logo", { data_url: dataUrl });
          setP({ ...p, logo_b64: dataUrl });
          toast.success("Logo terunggah");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLogoLoading(false); }
      };
      img.onerror = () => { setLogoLoading(false); toast.error("Gambar tidak valid"); };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    if (!window.confirm("Hapus logo saat ini?")) return;
    try { await api.delete("/workspace-profile/logo"); setP({ ...p, logo_b64: "" }); toast.success("Logo dihapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (<>
    <PageHeader title="Pengaturan Umum" subtitle="Konfigurasi bisnis, invoice, dan pajak."/>
    <div className="p-4 md:p-8 space-y-6 max-w-3xl">

      {isAdmin && (
        <div className="card-flat p-5" data-testid="section-invoice-profile">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={18} className="brand-text"/>
            <h3 className="font-semibold text-slate-900">Data Invoice / Profil Bisnis</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">Digunakan di header PDF invoice & laporan. Hanya Admin yang dapat mengubah.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="md:col-span-1 space-y-2">
              <Label>Logo Bisnis (Maks 100KB, 200×200 px)</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 flex flex-col items-center gap-3">
                {p.logo_b64 ? (
                  <img src={p.logo_b64} alt="logo" className="h-24 w-24 object-contain border border-slate-100" data-testid="logo-preview"/>
                ) : (
                  <div className="h-24 w-24 flex items-center justify-center bg-slate-50 text-slate-300 text-xs border border-slate-200">Belum ada logo</div>
                )}
                <label className="w-full cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-input"
                    onChange={(e) => validateAndUpload(e.target.files?.[0])}/>
                  <span className="brand-btn rounded-sm text-white text-xs inline-flex items-center gap-1 px-3 py-2 justify-center w-full">
                    <Upload size={12}/> {logoLoading ? "Mengunggah..." : (p.logo_b64 ? "Ganti Logo" : "Upload Logo")}
                  </span>
                </label>
                {p.logo_b64 && (
                  <button onClick={removeLogo} className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-1" data-testid="logo-remove">
                    <Trash2 size={11}/> Hapus logo
                  </button>
                )}
              </div>
            </div>
            <div className="md:col-span-2 space-y-3">
              <div className="space-y-1.5"><Label>Nama Bisnis / Perusahaan</Label><Input value={p.name || ""} onChange={(e)=>setP({...p, name: e.target.value})} placeholder="mis. HostNesia Indonesia" data-testid="inv-name"/></div>
              <div className="space-y-1.5"><Label>Alamat</Label><Textarea value={p.address || ""} onChange={(e)=>setP({...p, address: e.target.value})} rows={2} data-testid="inv-address"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>No. Telp</Label><Input value={p.phone || ""} onChange={(e)=>setP({...p, phone: e.target.value})} placeholder="+62..." data-testid="inv-phone"/></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={p.email || ""} onChange={(e)=>setP({...p, email: e.target.value})} placeholder="info@..." data-testid="inv-email"/></div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={loading} className="brand-btn rounded-sm" data-testid="save-invoice-profile">
              <Save size={14} className="mr-1"/> Simpan Data Invoice
            </Button>
          </div>
        </div>
      )}

      <div className="card-flat p-5">
        <h3 className="font-semibold text-slate-900 mb-1">Pengaturan Pajak (PPN)</h3>
        <p className="text-xs text-slate-500 mb-4">Aktifkan bila bisnis Anda PKP. PPN akan otomatis mengisi field pajak saat input pemasukan.</p>
        <div className="flex items-center justify-between border-b py-3">
          <div>
            <div className="text-sm font-medium">Status PKP (Pengusaha Kena Pajak)</div>
            <div className="text-xs text-slate-500">Jika aktif, PPN otomatis dihitung.</div>
          </div>
          <Switch checked={!!s.is_pkp} onCheckedChange={(v)=>setS({...s, is_pkp: v})} disabled={!isAdmin} data-testid="switch-pkp"/>
        </div>
        <div className="flex items-center justify-between py-3">
          <div>
            <div className="text-sm font-medium">PPN (%)</div>
            <div className="text-xs text-slate-500">Persen PPN yang otomatis diterapkan.</div>
          </div>
          <Input type="number" className="w-24 text-right" value={s.ppn_persen || 0} onChange={(e)=>setS({...s, ppn_persen: e.target.value})} disabled={!isAdmin || !s.is_pkp} data-testid="input-ppn"/>
        </div>
        {isAdmin && (
          <div className="flex justify-end mt-2">
            <Button onClick={saveSettings} disabled={loading} className="brand-btn rounded-sm" data-testid="save-settings">
              <Save size={14} className="mr-1"/> Simpan Pengaturan
            </Button>
          </div>
        )}
      </div>
    </div>
  </>);
}
